import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { TAG_COLORS, type TagColor } from "../types";
import { cx } from "../utils/format";

export interface TagChipData { id: string; name: string; color?: TagColor; /** the AI applied it and no person has touched it yet */ ai?: boolean;
  /** the customer tags round (2026-10-24): a tag on the ticket's customer, not on this ticket: a small person icon, the muted outline, the
   *  hover saying it follows the person (`title`). The ticket style round (2026-10-26, the founder overturns "no cross"): the remove cross
   *  when the app passes `onRemoveCustomer`, named for the person, since it untags them everywhere */
  customer?: boolean; title?: string; }

/** The dot that carries a tag's colour. Everything else about a tag stays gray. */
export function TagDot({ color }: { color?: TagColor }) {
  return <span className="ob-tag__dot" style={{ ["--tag" as string]: `var(--ob-tag-${color ?? "gray"})` }} aria-hidden="true" />;
}

/** A ticket's tags as quiet chips, each removable, with the picker's trigger at the end.
 *  The chip is gray and the colour is a dot: a tag is a word first. An AI-applied tag shows the AI sparkle in the dot's place, in the tag's own colour, until someone keeps it. */
export function TagChips({ tags, onRemove, onConfirm, add, customer }: { tags: TagChipData[]; onRemove: (id: string) => void; /** a click on an AI tag keeps it and drops the mark */ onConfirm?: (id: string) => void; /** the picker, usually a Menu around a TagAddButton */ add?: ReactNode;
  /** the ticket style round (2026-10-26): the customer chips' cross, for whoever may edit the customer: whose tags they are and the removal */ customer?: { name: string; onRemove: (id: string) => void } }) {
  return (
    <div className="ob-tags" role="list" aria-label="Tags">
      {tags.map((t) => t.customer ? <CustomerTagChip key={`c-${t.id}`} t={t} customer={customer} /> : (
        <span key={t.id} role="listitem" className={cx("ob-tag", t.ai && "ob-tag--ai")} style={{ ["--tag" as string]: `var(--ob-tag-${t.color ?? "gray"})` }}>
          {t.ai ? <Icon name="sparkles" size="xs" /> : <TagDot color={t.color} />}
          {t.ai && onConfirm ? <button type="button" className="ob-tag__name" title="Suggested by the AI · click to keep" onClick={() => onConfirm(t.id)}>{t.name}</button> : <span className="ob-tag__name">{t.name}</span>}
          <button type="button" className="ob-tag__remove" aria-label={`Remove tag ${t.name}`} onClick={() => onRemove(t.id)}><Icon name="x" size="xs" /></button>
        </span>
      ))}
      {add}
    </div>
  );
}

/** a customer's tag among a ticket's chips: the person icon in the tag's colour, the muted outline on the surface, the hover; the cross (when
 *  given) is named "Remove tag <tag> from <customer>", so nobody takes it for the ticket's own */
function CustomerTagChip({ t, customer }: { t: TagChipData; customer?: { name: string; onRemove: (id: string) => void } }) {
  return (
    <span role="listitem" className="ob-tag ob-tag--customer" title={t.title} style={{ ["--tag" as string]: `var(--ob-tag-${t.color ?? "gray"})` }}>
      <Icon name="user" size="xs" /><span className="ob-tag__name">{t.name}</span>
      {customer && <button type="button" className="ob-tag__remove" aria-label={`Remove tag ${t.name} from ${customer.name}`} onClick={() => customer.onRemove(t.id)}><Icon name="x" size="xs" /></button>}
    </span>
  );
}

/** The "+ Tag" chip that opens the picker. Forwards its ref and props so a Menu can use it as its trigger. */
export const TagAddButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(function TagAddButton(props, ref) {
  return <button ref={ref} type="button" className="ob-tag ob-tag--add" aria-label="Add tag" {...props}><Icon name="plus" size="xs" />Tag<kbd className="ob-tag__key">T</kbd></button>;
});

/** Twelve swatches as a radio group, for the new-tag and edit-tag dialogs. */
export function TagColorPicker({ value, onChange }: { value: TagColor; onChange: (c: TagColor) => void }) {
  return (
    <div className="ob-tagcolors" role="radiogroup" aria-label="Colour">
      {TAG_COLORS.map((c) => <button key={c} type="button" role="radio" aria-checked={c === value} aria-label={c} title={c} className={cx("ob-tagcolors__swatch", c === value && "ob-tagcolors__swatch--on")} style={{ ["--tag" as string]: `var(--ob-tag-${c})` }} onClick={() => onChange(c)} />)}
    </div>
  );
}
